import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal, IconButton } from "@mui/material";
import { Box } from "@mui/system";
import Board from "@publicPortal/components/board/Board";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import React, { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import UserCollectedDataHelper from "../../UserCollectedDataHelper";
import { EditSavedSearch } from "./PlanningApplicationEditSearch";
import BuildingRegulationsService from "@publicPortal/services/BuildingRegulationsService";
import PublicPortalState from "@publicPortal/utils/InitialState";
import BuildingRegulationsSearchState from "@publicPortal/pages/search/BuildingRegulations/BuildingRegulationsSearchState";
import { ValidationResult } from "@shared/mirage/validators";
import { IsStringNotEmptyValidator } from "@shared/mirage/validators/IsStringNotEmptyValidator";
import BuildingRegulationsSimpleSearch from "@publicPortal/pages/search/BuildingRegulations/BuildingRegulationsSimpleSearch";
import MapFallback from "@mirage/components/MapView/components/MapFallback";
import { BuildingRegulationsFilter } from "@shared/publicportal/contracts/BuildingRegulationsFilter";
import { SearchType } from "@shared/publicportal/contracts/Search";
import SearchActions from "@publicPortal/pages/search/SearchActions";
import BuildingRegulationsAdvancedSearch from "@publicPortal/components/advancedSearch/BuildingRegulationsAdvancedSearch";
import { PPConstants } from "@shared/publicportal/constants";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { GAIA_DRAW_MODE, TileLayerOptions } from "@mirage/components/gaiaMap/GaiaMap";

const GaiaMap = lazy(() =>
    import("@mirage/components/gaiaMap/GaiaMap").then((module) => {
        return { default: module.GaiaMap };
    })
);
const brService = new BuildingRegulationsService();

const BuildingRegulationEditSearch: React.FC<EditSavedSearch> = ({ closeModal, criteria, isOpen, saveEditSearch }) => {
    const initialValues = criteria.jsonSearchParameters && JSON.parse(criteria.jsonSearchParameters);
    const { initialSearchState, initialFilterState } = PublicPortalState.buildingRegulationsSearchState();
    const initSearchState: BuildingRegulationsSearchState = {
        ...initialSearchState,
        filter: {
            ...initialFilterState,
            ...initialValues,
        },
    };
    const [state, setState] = useState<BuildingRegulationsSearchState>({} as BuildingRegulationsSearchState);
    const [searchName, setSearchName] = useState("");
    const [searchNameValidation, setSearchNameValidation] = useState<ValidationResult>({ passed: true, error: "" });
    const handleSearchName = (value: string) => {
        const validationResult = IsStringNotEmptyValidator(value, "Enter Search name");
        setSearchNameValidation(validationResult);
        setSearchName(value);
    };
    const boardSize = initialValues?.searchType === "Advanced" ? "big" : "small";

    useEffect(() => {
        setState(initSearchState);
        setSearchName(criteria.searchName);
        setSearchNameValidation({ passed: true, error: "" });
        brService.getFilteringLists().then((filter) => {
            setState((prev) => {
                return {
                    ...prev,
                    appTypes: filter.ApplicationTypes,
                    partnershipTypes: filter.PartnershipTypes,
                    statusTypes: filter.Statuses,
                    wards: filter.Wards,
                    parishes: filter.Parishes,
                };
            });
        });
    }, [criteria, closeModal]);

    return (
        <>
            <Modal open={isOpen}>
                <Box height={"100%"}>
                    <Board id={boardSize === "big" ? "editSearch__bigBoard" : "editSearch__smallBoard"} size={boardSize} style={getBoardStyles()}>
                        <Box className="editSearch__headerContainer">
                            {UserCollectedDataHelper.getSearchName(searchName, handleSearchName, searchNameValidation)}
                            <IconButton onClick={closeModal} className="close-saved-search-modal-button">
                                <FontAwesomeIcon icon={faXmark} style={PublicPortalTheme.icons.sizeM} />
                            </IconButton>
                        </Box>
                        <BuildingRegulationEditSearchForm setter={setStateModifier} state={state} searchType={initialValues?.searchType} />
                        <SearchActions onSubmitHandler={handleSave} onCancelHandler={closeModal} customSubmitName={"Save"} customCancelName={"Cancel"} />
                    </Board>
                </Box>
            </Modal>
        </>
    );

    async function handleSave() {
        if (searchNameValidation.passed) {
            await saveEditSearch(criteria.id, searchName, criteria.searchType, state.filter);
        }
    }

    function getBoardStyles() {
        if (initialValues?.searchType === "Map") {
            const mapBoardStyles = {
                width: "75%",
            };

            return mapBoardStyles;
        }
    }

    function setStateModifier(modifier: Partial<BuildingRegulationsFilter>) {
        setState((prev) => {
            return { ...prev, filter: { ...prev.filter, ...modifier } };
        });
    }
};

const BuildingRegulationEditSearchForm: React.FC<{
    state: BuildingRegulationsSearchState;
    setter: (modifier: Partial<BuildingRegulationsFilter>) => void;
    searchType?: SearchType;
}> = ({ state, setter, searchType }) => {
    switch (searchType) {
        case "Simple": {
            return <BuildingRegulationsSimpleSearch state={state} setter={setter} />;
        }
        case "Advanced": {
            return <BuildingRegulationsAdvancedSearch state={state} setter={setter} />;
        }
        case "Map": {
            return <MapSearchEdit geometry={state.filter.geometry} setter={setter} />;
        }
        default:
            return <BuildingRegulationsSimpleSearch state={state} setter={setter} />;
    }
};

const MapSearchEdit: React.FC<{ setter: (modifier: Partial<BuildingRegulationsFilter>) => void; geometry?: string }> = memo(({ geometry, setter }) => {
    const [sessionValue] = useSessionStorage(PPConstants.AppName);
    const { clientSettings } = sessionValue;
    const masterLayer: TileLayerOptions | undefined = useMemo(
        () =>
            clientSettings
                ? {
                      layerUrl: clientSettings.MASTER_LAYER_URL,
                      attribution: clientSettings.MASTER_LAYER_ATTRIBUTION,
                  }
                : undefined,
        [clientSettings]
    );
    const onChange = useCallback((geometry: string | null) => setter({ geometry: geometry || "" }), [setter]);
    const enabledToolTypes = useMemo(() => [GAIA_DRAW_MODE.POLYGON], []);

    return (
        <Suspense fallback={<MapFallback />}>
            <GaiaMap geometryJson={geometry} onChange={onChange} tileLayer={masterLayer} height={500} enabledToolTypes={enabledToolTypes} />
        </Suspense>
    );
});

export default BuildingRegulationEditSearch;
